// Registre des modules fonctionnels de l'interface.
// Chaque module expose `src/modules/<nom>/index.tsx` qui exporte `module: FrontModule`.
// Le socle les découvre automatiquement : aucun fichier central à modifier.
import type { RouteObject } from "react-router";
import type { Perm } from "./session";

export type NavItem = {
  /** Chemin relatif à l'espace, ex. « logements » → /espace/:orgId/logements */
  to: string;
  label: string;
  /** Rubrique de la barre latérale. */
  section: "Location" | "Gestion" | "Vie du logement" | "Finances" | "Immeubles" | "Professionnels" | "Espace";
  perm?: Perm;
  order?: number;
  /** Rubrique refusée par le serveur à un collaborateur au périmètre restreint (F-091) : masquée pour lui. */
  hideWhenRestricted?: boolean;
};

export type FrontModule = {
  name: string;
  /** Routes enfants de /espace/:orgId (chemins relatifs). */
  orgRoutes?: RouteObject[];
  nav?: NavItem[];
  /** Routes publiques (ex. page de candidature /candidature/:slug). */
  publicRoutes?: RouteObject[];
  /** Routes pour un compte connecté hors espace (candidat, locataire), sous /moi. */
  accountRoutes?: RouteObject[];
  /** Liens affichés dans l'espace personnel (/moi). */
  accountNav?: { to: string; label: string }[];
  /** Bloc affiché sur le tableau de bord de l'espace. */
  Dashboard?: React.ComponentType<{ orgId: string }>;

  // Points d'extension : un module ajoute un onglet ou une section dans une page détenue par un autre module.
  /** Fiche logement (page du module patrimoine). */
  propertyTabs?: ExtTab<{ orgId: string; propertyId: string }>[];
  /** Campagne de location (page du module location). */
  campaignTabs?: ExtTab<{ orgId: string; campaignId: string }>[];
  /** Candidature côté bailleur (page du module location). */
  applicationTabs?: ExtTab<{ orgId: string; applicationId: string }>[];
  /** Candidature côté candidat /moi/candidatures/:id (page du module location). */
  candidateSections?: ExtTab<{ applicationId: string }>[];
  /** Bail côté bailleur (page du module entrée). */
  leaseTabs?: ExtTab<{ orgId: string; leaseId: string }>[];
  /** Location côté locataire /moi/locations/:leaseId (page du module entrée). */
  tenantSections?: ExtTab<{ leaseId: string }>[];
};

export type ExtTab<P> = {
  id: string;
  label: string;
  order?: number;
  Component: React.ComponentType<P>;
  /** Condition d'affichage, évaluée avec l'élément chargé par la page hôte (candidature, bail…). */
  visible?: (ctx: ExtContext) => boolean;
  /** Onglet appuyé sur une vue refusée à un collaborateur restreint (F-091) : masqué pour lui. */
  hideWhenRestricted?: boolean;
};

/** Contexte transmis par la page hôte : l'élément affiché et, côté locataire ou candidat, le rôle de la personne. */
export type ExtContext = { data: any; role?: string; restricted?: boolean };

type ExtKey = "propertyTabs" | "campaignTabs" | "applicationTabs" | "candidateSections" | "leaseTabs" | "tenantSections";

/** Rassemble les extensions déclarées par tous les modules pour un point donné, triées.
 *  Avec un contexte, les extensions dont la condition `visible` est fausse sont écartées. */
export function extensions<K extends ExtKey>(key: K, ctx?: ExtContext): NonNullable<FrontModule[K]> {
  const all = modules.flatMap((m) => (m[key] ?? []) as ExtTab<any>[]).filter((t) => !ctx || ((!t.visible || t.visible(ctx)) && !(ctx.restricted && t.hideWhenRestricted)));
  return all.sort((a, b) => (a.order ?? 50) - (b.order ?? 50)) as NonNullable<FrontModule[K]>;
}

const found = import.meta.glob<{ module: FrontModule }>("../modules/*/index.tsx", { eager: true });

export const modules: FrontModule[] = Object.values(found)
  .map((m) => m.module)
  .filter(Boolean)
  .sort((a, b) => a.name.localeCompare(b.name));

export const SECTIONS: NavItem["section"][] = ["Location", "Gestion", "Vie du logement", "Finances", "Immeubles", "Professionnels", "Espace"];
